import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
} from "react";

import { cn } from "@/components/ui/utils";

/** The pattern drawn on its ground: a style, as nothing under the pointer changes it. */
const getBackgroundPatternStyle = (backgroundPattern: string) => {
  return {
    ...(backgroundPattern === "dots" && {
      backgroundImage: `radial-gradient(${"var(--border)"} 1px, transparent 1px)`,
      backgroundSize: "12px 12px",
    }),
    ...(backgroundPattern === "lines" && {
      backgroundImage: `repeating-linear-gradient(90deg, ${"var(--input)"} 0px, ${"var(--input)"} 1px, transparent 1px, transparent 16px)`,
      backgroundSize: "auto",
    }),
    ...(backgroundPattern === "grid" && {
      "--grid-size": "24px",
      "--grid-strength": "1px",
      "--grid-gap": "calc(var(--grid-size) / 4)",
      "--grid-dash": "4px",
      "--grid-color": "var(--border)",
      "--paper-color": "var(--background)",
      "--solid-line-size": "calc(var(--grid-size) * 5)",
      backgroundImage: `
        linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px),
        linear-gradient(to right, var(--grid-color) 1px, transparent 1px),
        linear-gradient(to bottom, transparent var(--grid-dash), var(--paper-color) var(--grid-dash)), 
        linear-gradient(to right, var(--grid-color) var(--grid-strength), transparent var(--grid-strength)),
        linear-gradient(to right, transparent var(--grid-dash), var(--paper-color) var(--grid-dash)),
        linear-gradient(to bottom, var(--grid-color) var(--grid-strength), transparent var(--grid-strength))
      `,
      backgroundSize: `
        var(--solid-line-size) var(--solid-line-size), 
        var(--solid-line-size) var(--solid-line-size), 
        var(--grid-gap) var(--grid-gap), 
        var(--grid-size) var(--grid-size), 
        var(--grid-gap) var(--grid-gap), 
        var(--grid-size) var(--grid-size)
      `,
      backgroundPosition: "center center",
    }),
    ...(backgroundPattern === "squares" && {
      "--square-size": "16px",
      "--gap-size": "8px",
      "--square-total-size": "calc(var(--square-size) + var(--gap-size))",
      "--square-color-val": "var(--border)",
      // This creates a grid of squares. The color is theme.palette.divider.
      // Note: Randomly and independently animating opacity for each square is complex
      // with pure CSS backgrounds. A synchronized animation or JS would be needed for that.
      backgroundImage: `
        repeating-linear-gradient(0deg, var(--square-color-val) 0, var(--square-color-val) var(--square-size), transparent var(--square-size), transparent var(--square-total-size)),
        repeating-linear-gradient(90deg, var(--square-color-val) 0, var(--square-color-val) var(--square-size), transparent var(--square-size), transparent var(--square-total-size))
      `,
      backgroundSize: "var(--square-total-size) var(--square-total-size)",
    }),
  } as CSSProperties;
};

const TARGET_FADE_OPACITY = 1; // Adjust as needed for fade intensity

// Moved hexToRgb outside of the component or useEffect for stability and to avoid re-creation.
// Made it slightly more robust.
const hexToRgb = (hex: string): string => {
  let r = 0,
    g = 0,
    b = 0;
  const cleanHex = hex.startsWith("#") ? hex.substring(1) : hex;

  if (cleanHex.length === 3) {
    // RGB
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else if (cleanHex.length === 6) {
    // RRGGBB
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  } else {
    // If hex is not 3 or 6 chars after removing #, defaults to 0,0,0 (black)
    // console.warn(`hexToRgb received unhandled format: ${hex}. Defaulting to black.`);
  }
  // Ensure NaN values are handled, defaulting to 0 for a component if parsing failed.
  return `${isNaN(r) ? 0 : r}, ${isNaN(g) ? 0 : g}, ${isNaN(b) ? 0 : b}`;
};

export function BoxWithAnimatedBackground({
  backgroundPattern = "none",
  fadeIn = true,
  backgroundColor,
  className,
  style,
  innerClassName,
  innerStyle,
  children,
  ...props
}: {
  backgroundPattern: "none" | "dots" | "lines" | "grid" | "squares";
  fadeIn?: boolean;
  /** Its ground, and what its edges fade into: the page's own unless told. */
  backgroundColor?: string;
  /** The box's own - its size, its edge, its hover. */
  className?: string;
  style?: CSSProperties;
  /** What holds its children, over the pattern. */
  innerClassName?: string;
  innerStyle?: CSSProperties;
  children: React.ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "children">) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentOverlayOpacity, setCurrentOverlayOpacity] = useState(0);

  useEffect(() => {
    let animationFrameId: number;
    if (fadeIn) {
      // Use requestAnimationFrame to ensure the opacity change happens after the initial paint
      animationFrameId = requestAnimationFrame(() => {
        setCurrentOverlayOpacity(TARGET_FADE_OPACITY);
      });
    } else {
      setCurrentOverlayOpacity(0);
    }
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [fadeIn]);

  // Its edges faded into its ground - drawn over the pattern, under what it
  // holds.
  const fade = {
    "--fade-color": backgroundColor ?? "var(--background)",
    "--fade-opacity": currentOverlayOpacity,
  } as CSSProperties;
  const FADE =
    "before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-full before:[box-shadow:inset_0_0_80px_90px_var(--fade-color)] before:opacity-(--fade-opacity) before:pointer-events-none before:z-1";

  useEffect(() => {
    if (backgroundPattern !== "squares") {
      // If the pattern is not squares, or changes from squares to something else,
      // this effect instance is done or doesn't apply.
      // The cleanup from a previous "squares" instance (if any) will be handled by React
      // when that effect instance is destroyed due to backgroundPattern change.
      return;
    }

    // Guard against SSR or if canvasRef is not yet populated.
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    let animationFrameId: number;

    const squareSize = 8; // Logical pixels
    const gapSize = 4; // Logical pixels
    const totalSize = squareSize + gapSize; // Logical pixels
    const squareColor = "#adb2b6"; // Reverted to original color

    let squaresState: Array<
      Array<{ opacity: number; targetOpacity: number; speed: number }>
    > = [];

    const initializeSquares = (
      currentCanvasWidth: number,
      currentCanvasHeight: number,
    ) => {
      // Calculate cols/rows based on logical dimensions of the canvas
      const logicalWidth = currentCanvasWidth / dpr;
      const logicalHeight = currentCanvasHeight / dpr;

      const numCols = Math.ceil(logicalWidth / totalSize);
      const numRows = Math.ceil(logicalHeight / totalSize);

      squaresState = [];
      for (let r = 0; r < numRows; r++) {
        squaresState[r] = [];
        for (let c = 0; c < numCols; c++) {
          squaresState[r][c] = {
            opacity: Math.random() * 0.75,
            targetOpacity: Math.random() * 0.75,
            // Increased speed for more visible animation
            speed: 0.003,
          };
        }
      }
    };

    const draw = () => {
      const rect = canvas.getBoundingClientRect(); // Get current CSS pixel dimensions
      const displayWidth = rect.width; // Logical width for drawing calculations (CSS pixels)
      const displayHeight = rect.height; // Logical height for drawing calculations (CSS pixels)

      // Calculate buffer dimensions
      const newBufferWidth = Math.round(displayWidth * dpr);
      const newBufferHeight = Math.round(displayHeight * dpr);

      if (
        canvas.width !== newBufferWidth ||
        canvas.height !== newBufferHeight
      ) {
        canvas.width = newBufferWidth;
        canvas.height = newBufferHeight;
        // Re-initialize squares if canvas buffer size changes
        initializeSquares(newBufferWidth, newBufferHeight);
      }

      // If context got lost or was never there (e.g. after re-init if something went wrong)
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Only proceed if there are squares to draw
      if (
        squaresState.length === 0 ||
        (squaresState[0] && squaresState[0].length === 0)
      ) {
        animationFrameId = requestAnimationFrame(draw);
        return;
      }

      ctx.save();
      ctx.scale(dpr, dpr); // Scale context to draw in logical pixels

      const numRows = squaresState.length;
      const numCols = squaresState[0]?.length || 0;

      // Calculate the actual width/height of the drawn content for centering
      const actualContentWidth =
        numCols > 0 ? numCols * totalSize - gapSize : 0;
      const actualContentHeight =
        numRows > 0 ? numRows * totalSize - gapSize : 0;

      const offsetX = (displayWidth - actualContentWidth) / 2;
      const offsetY = (displayHeight - actualContentHeight) / 2;

      ctx.translate(offsetX, offsetY); // Translate for centering

      for (let r = 0; r < numRows; r++) {
        for (let c = 0; c < numCols; c++) {
          const square = squaresState[r]?.[c]; // Optional chaining for safety
          if (!square) continue;

          // Original opacity animation logic - RESTORED
          if (Math.abs(square.opacity - square.targetOpacity) < 0.01) {
            square.targetOpacity = Math.random();
          }
          if (square.opacity < square.targetOpacity) {
            square.opacity = Math.min(
              square.opacity + square.speed,
              square.targetOpacity,
            );
          } else {
            square.opacity = Math.max(
              square.opacity - square.speed,
              square.targetOpacity,
            );
          }

          ctx.fillStyle = `rgba(${hexToRgb(squareColor)}, ${square.opacity})`;
          ctx.fillRect(c * totalSize, r * totalSize, squareSize, squareSize);
        }
      }

      ctx.restore(); // Restore context (scale and translate)
      // frameCounter++; // Increment frame counter for opacity test - REMOVED
      animationFrameId = requestAnimationFrame(draw);
    };

    // Initial setup: Get initial CSS dimensions and initialize
    const initialRect = canvas.getBoundingClientRect();
    const initialBufferWidth = Math.round(initialRect.width * dpr);
    const initialBufferHeight = Math.round(initialRect.height * dpr);

    canvas.width = initialBufferWidth;
    canvas.height = initialBufferHeight;
    // canvas.style.width and canvas.style.height are already 100% from inline style

    initializeSquares(initialBufferWidth, initialBufferHeight);
    draw(); // Start the animation loop

    const resizeObserver = new ResizeObserver(() => {
      // The draw function itself handles resizing the canvas buffer based on getBoundingClientRect.
      // This observer is mainly here to ensure the animation loop continues and adapts
      // if the parent container resizes. The check within draw() will handle re-initialization.
      // No explicit canvas.width/height setting needed here as draw() does it.
    });

    // Observe the canvas's parent element for resizes.
    // If the parent resizes, the canvas (width: 100%, height: 100%) will resize,
    // and getBoundingClientRect() in draw() will pick it up.
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (canvas.parentElement) {
        resizeObserver.unobserve(canvas.parentElement);
      }
      // Optional: Clear canvas on cleanup if needed, though not strictly necessary
      // if the component is unmounted or pattern changes.
      // ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [backgroundPattern]); // Corrected dependency array

  if (backgroundPattern === "squares") {
    return (
      <div
        {...props}
        className={cn(
          // Its canvas placed in it, and clipped to it.
          "relative overflow-hidden",
          backgroundColor && "bg-(--ground)",
          className,
          FADE,
        )}
        style={
          { ...style, "--ground": backgroundColor, ...fade } as CSSProperties
        }
      >
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "block", // Removes extra space below canvas
          }}
        />
        <div className="relative z-2">{children}</div>
      </div>
    );
  }

  // Fallback for other patterns
  return (
    <div
      {...props}
      className={cn(
        backgroundColor && "bg-(--ground)",
        className,
        // The grid's paper is its ground, over the box's own.
        backgroundPattern === "grid" && "bg-(--paper-color)",
        "relative",
        FADE,
      )}
      style={
        {
          ...style,
          "--ground": backgroundColor,
          ...getBackgroundPatternStyle(backgroundPattern),
          ...fade,
        } as CSSProperties
      }
    >
      <div className={cn("relative z-2", innerClassName)} style={innerStyle}>
        {children}
      </div>
    </div>
  );
}
